// MicVolumeMeter.jsx
import React, { useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import { NULL_FUNCTION } from '../resource/defaults.js';
import { color } from '../style/variables.js';

// Mic volume meter: samples the audio level (0-1, e.g. Agora getVolumeLevel)
// each frame and drives four bar heights (not pure CSS). The whole badge is
// visible only while sound is detected. Each PEAK_VOLUME_HEIGHTS needs a
// matching DEFAULT one.
const DEFAULT_VOLUME_HEIGHTS = [5, 4, 7, 5];
const PEAK_VOLUME_HEIGHTS = [10, 8, 14, 10];
const MAX_PEAK_HEIGHT = Math.max(...PEAK_VOLUME_HEIGHTS);
const NOISE_GATE_LEVEL = 0.6; // Raw levels at/below this are ambient noise → treated as silence
const VISIBLE_HOLD_MS = 500; // Stay visible through short pauses between words
const SMOOTHING_FACTOR = 0.5; // Per-frame interpolation toward target (higher = snappier)
const JITTER_MIN = 0.05;
const JITTER_RANGE = 2.0;
const UPDATE_INTERVAL_MS = 1000 / 15;

const MicVolumeMeter = ({ getAudioLevel = NULL_FUNCTION }) => {
  const badgeRef = useRef(null);
  const barRefs = useRef([]);

  useEffect(() => {
    // Smooth each bar (avoid flicker); start from the default height
    const smoothedHeights = DEFAULT_VOLUME_HEIGHTS.slice();
    let rafId = null;
    let lastUpdatedAt = 0;
    // Hidden until the first sound is detected.
    let lastSoundDetectedAt = -Infinity;

    const tick = now => {
      rafId = requestAnimationFrame(tick);
      if (now - lastUpdatedAt < UPDATE_INTERVAL_MS) return;
      lastUpdatedAt = now;

      // Gate out ambient noise, then re-normalize the remaining range to 0..1
      const rawLevel = getAudioLevel() || 0;
      const volume = Math.min(
        Math.max(rawLevel - NOISE_GATE_LEVEL, 0) / (1 - NOISE_GATE_LEVEL),
        1
      );

      if (volume > 0) {
        lastSoundDetectedAt = now;
      }
      if (badgeRef.current) {
        badgeRef.current.style.visibility =
          now - lastSoundDetectedAt <= VISIBLE_HOLD_MS ? 'visible' : 'hidden';
      }

      DEFAULT_VOLUME_HEIGHTS.forEach((defaultHeight, barIndex) => {
        // Grow with volume (× random jitter for irregular motion), capped at peak
        const peakHeight = PEAK_VOLUME_HEIGHTS[barIndex];
        const targetHeight = Math.min(
          defaultHeight +
            volume *
              (JITTER_MIN + Math.random() * JITTER_RANGE) *
              (peakHeight - defaultHeight),
          peakHeight
        );
        smoothedHeights[barIndex] +=
          (targetHeight - smoothedHeights[barIndex]) * SMOOTHING_FACTOR;
        const barNode = barRefs.current[barIndex];
        if (barNode) {
          barNode.style.height = `${smoothedHeights[barIndex]}px`;
        }
      });
    };
    rafId = requestAnimationFrame(tick);

    return () => {
      cancelAnimationFrame(rafId);
    };
  }, [getAudioLevel]);

  return (
    <Badge ref={badgeRef}>
      {DEFAULT_VOLUME_HEIGHTS.map((defaultHeight, barIndex) => (
        <Bar
          key={barIndex}
          $defaultHeight={defaultHeight}
          ref={node => {
            barRefs.current[barIndex] = node;
          }}
        />
      ))}
    </Badge>
  );
};

MicVolumeMeter.propTypes = {
  getAudioLevel: PropTypes.func,
};

const BADGE_VERTICAL_PADDING = 4;
const Badge = styled.div`
  box-sizing: border-box;
  display: flex;
  align-items: center;
  gap: 2px;
  height: ${MAX_PEAK_HEIGHT + BADGE_VERTICAL_PADDING * 2}px;
  padding: 0 6px;
  border-radius: 4px;
  background: ${color.neutral['0_alpha_40']};
  visibility: hidden;
`;

const Bar = styled.span`
  flex: none;
  width: 2px;
  height: ${({ $defaultHeight }) => $defaultHeight}px;
  border-radius: 2px;
  background: ${color.tealBlue};
`;

export default MicVolumeMeter;
